/** PANOUL DE DETALII al elementului ales pe machetă (Cătălin, 13.10.2026).
 *
 *  Apare NUMAI cât e ales un element și dispare odată cu selecția. Seamănă cu
 *  asistentul (aceeași fereastră plutitoare, aceeași temă), dar stă în stânga
 *  jos, ca să nu se bată cu discuția din dreapta. Arată ce scrie documentul
 *  despre element (v. `detalii.ts`); cotele de pe machetă le pornește și le
 *  oprește butonul din bara de sus. Cu mai multe elemente alese, săgețile trec
 *  de la unul la altul.
 *
 *  COSTUL elementului se cere la selecție (`costElement.ts` → serverul, cu
 *  tarifele devizului fațadei) și se ține minte cât documentul e același. */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { costElemente, type CostElemente } from "../../api";
import { bcp47, esteCheie, limba, numar, useT, type Cheie } from "../../i18n";
import { bani } from "../../structure/bani";
import type { CanonicalDoc } from "../canonical";
import { cerereCost } from "./costElement";
import { detaliiElement } from "./detalii";
import "./detalii.css";

interface Props {
  projectId: string;
  doc: CanonicalDoc | null;
  /** Elementele alese, în ordinea alegerii. */
  elemente: readonly string[];
  /** Cel arătat (și cotat). */
  activ: string;
  laActiv: (eid: string) => void;
  /** Închiderea golește selecția: panoul trăiește cât ea. */
  laInchide: () => void;
  cote: boolean;
  laCote: (da: boolean) => void;
}

const Icon = ({ d, marime = 18 }: { d: string; marime?: number }) => (
  <svg width={marime} height={marime} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
);

/** Costul elementului activ: `undefined` cât se cere, `null` fără cost. */
function useCost(projectId: string, doc: CanonicalDoc | null, eid: string) {
  const memorie = useRef<{ doc: CanonicalDoc | null; cost: Map<string, CostElemente | null> }>(
    { doc: null, cost: new Map() });
  if (memorie.current.doc !== doc) memorie.current = { doc, cost: new Map() };
  const [, reda] = useState(0);
  const tinut = memorie.current.cost.get(eid);
  useEffect(() => {
    const m = memorie.current;
    if (m.cost.has(eid)) return;
    const cerere = cerereCost(doc, eid);
    if (!cerere) { m.cost.set(eid, null); reda((x) => x + 1); return; }
    let viu = true;
    costElemente(projectId, cerere)
      .then((c) => { m.cost.set(eid, c); })
      .catch(() => { m.cost.set(eid, null); })
      .finally(() => { if (viu) reda((x) => x + 1); });
    return () => { viu = false; };
  }, [projectId, doc, eid]);
  return memorie.current.cost.has(eid) ? tinut ?? null : undefined;
}

export default function DetaliiElement({ projectId, doc, elemente, activ, laActiv, laInchide, cote, laCote }: Props) {
  const t = useT();
  const tr = (k: string, rezerva: string) => (esteCheie(k) ? t(k as Cheie) : rezerva);
  const det = useMemo(() => detaliiElement(
    doc, activ, bcp47(limba()),
    (m) => tr(`material.${m}`, m.replace(/_/g, " ")),
    (g, v) => tr(`detalii.v.${g}.${v}`, v.replace(/_/g, " ")),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [doc, activ]);
  const cost = useCost(projectId, doc, activ);
  const sub = det ? [
    det.material,
    det.unde.etaj !== undefined ? (det.unde.etaj === 0 ? t("detalii.v.etaj.parter")
                                   : `${t("detalii.etaj")} ${det.unde.etaj}`) : "",
  ].filter(Boolean).join(" · ") : "";
  const i = Math.max(0, elemente.indexOf(activ));
  const n = elemente.length;

  return createPortal(
    <div className="ob" style={{ minHeight: 0, height: 0, background: "none" }}>
      <section className="ob-det" aria-label={t("detalii.titlu_panou")}>
        <header className="ob-det-sus">
          {det?.culoare
            ? <span className="ob-det-mostra" style={{ background: det.culoare }} />
            : <span className="ob-det-mostra gol" />}
          <div className="ob-det-cap">
            <div className="ob-det-titlu">{det ? majuscula(det.titlu) : t("detalii.titlu_panou")}</div>
            {sub && <div className="ob-det-sub" title={sub}>{sub}</div>}
          </div>
          {n > 1 && (
            <div className="ob-det-nav">
              <button className="ob-det-icon" title={t("detalii.anterior")} aria-label={t("detalii.anterior")}
                      onClick={() => laActiv(elemente[(i - 1 + n) % n])}><Icon d="M14.5 6l-6 6 6 6" /></button>
              <span className="ob-det-din">{t("detalii.din", { i: i + 1, n })}</span>
              <button className="ob-det-icon" title={t("detalii.urmator")} aria-label={t("detalii.urmator")}
                      onClick={() => laActiv(elemente[(i + 1) % n])}><Icon d="M9.5 6l6 6-6 6" /></button>
            </div>
          )}
          <button className={`ob-det-icon${cote ? " aprins" : ""}`} title={t("detalii.cote_titlu")}
                  aria-pressed={cote} aria-label={t("detalii.cote_titlu")} onClick={() => laCote(!cote)}>
            {/* rigla */}
            <Icon d="M3 15.5L15.5 3 21 8.5 8.5 21zM7 11.5l1.8 1.8M9.5 9l2.6 2.6M12 6.5l1.8 1.8M14.5 4" />
          </button>
          <button className="ob-det-icon" title={t("detalii.inchide")} aria-label={t("detalii.inchide")}
                  onClick={laInchide}><Icon d="M6 6l12 12M18 6L6 18" /></button>
        </header>
        <div className="ob-det-corp">
          {!det && <p className="ob-det-lipsa">{t("detalii.lipsa")}</p>}
          {det?.grupuri.map((g) => (
            <div key={g.cheie} className="ob-det-grup">
              <h4>{tr(`detalii.grup.${g.cheie}`, g.cheie)}</h4>
              <dl>
                {g.randuri.map((r) => (
                  <div key={r.cheie} className="ob-det-rand">
                    <dt>{tr(`detalii.${r.cheie}`, r.cheie)}</dt>
                    <dd>
                      {r.culoare && <span className="ob-det-mostra mica" style={{ background: r.culoare }} />}
                      {r.valoare}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
          {det && cost !== null && <GrupCost cost={cost} />}
        </div>
      </section>
    </div>,
    document.body,
  );
}

const majuscula = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

const UM: Record<string, string> = { m2: "m²", ml: "ml" };

/** COSTURILE elementului: material, manoperă, total (direct, fără sporuri),
 *  apoi pe ce se duc — un rând pe articol, cu cantitatea lui. */
function GrupCost({ cost }: { cost: CostElemente | undefined }) {
  const t = useT();
  const tr = (k: string, rezerva: string) => (esteCheie(k) ? t(k as Cheie) : rezerva);
  const eur = (v: number) => bani(v, { zecimale: 0 });
  return (
    <div className="ob-det-grup ob-det-cost">
      <h4>{t("detalii.grup.cost")}</h4>
      {cost === undefined ? <p className="ob-det-lipsa">{t("detalii.cost.se_calculeaza")}</p> : (
        <>
          <dl>
            <div className="ob-det-rand"><dt>{t("detalii.cost.material")}</dt><dd>{eur(cost.total_material_eur)}</dd></div>
            <div className="ob-det-rand"><dt>{t("detalii.cost.manopera")}</dt><dd>{eur(cost.total_manopera_eur)}</dd></div>
            <div className="ob-det-rand total"><dt>{t("detalii.cost.total")}</dt><dd>{eur(cost.total_direct_eur)}</dd></div>
          </dl>
          {cost.articole.length > 1 && (
            <dl className="ob-det-defalcare">
              {cost.articole.map((a) => (
                <div key={a.nr} className="ob-det-rand">
                  <dt>
                    {tr(`bc.fat.art.${a.articol}`, a.articol.replace(/_/g, " "))}
                    {a.grosime_m != null && ` ${numar(a.grosime_m * 100, { maximumFractionDigits: 0 })} cm`}
                    <span className="ob-det-cant">
                      {numar(a.cantitate, { maximumFractionDigits: 2 })} {UM[a.um] ?? a.um}
                    </span>
                  </dt>
                  <dd>{eur(a.total_eur)}</dd>
                </div>
              ))}
            </dl>
          )}
          {cost.articole.length === 1 && (
            <p className="ob-det-nota">
              {numar(cost.articole[0].cantitate, { maximumFractionDigits: 2 })} {UM[cost.articole[0].um] ?? cost.articole[0].um}
              {" × "}{bani(cost.articole[0].pu_material_eur + cost.articole[0].pu_manopera_eur, { zecimale: 2 })}
              /{UM[cost.articole[0].um] ?? cost.articole[0].um}
            </p>
          )}
          <p className="ob-det-nota">{t("detalii.cost.nota")}</p>
        </>
      )}
    </div>
  );
}
